import { Box, Button, Typography, Grid, Popover } from "@mui/material";
import { useSelector } from "react-redux";
import { RootState } from "../store/Index";
import PropTypes from "prop-types";
import { HexColorPicker } from "react-colorful";
import { useRef, useState } from "react";

interface ColorPickerProps {
  value: string;
  onChange: (color: string) => void;
  label?: string;
  sx?: Record<string, unknown>;
}

const predefinedColors = ["#FFFFFF", "#000000", "#614338"];

const ColorPicker: React.FC<ColorPickerProps> = ({
  value,
  onChange,
  label = "Color",
  sx,
}) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  const isValidHex = (hex: string): boolean => {
    return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(hex);
  };

  const reduxTheme = useSelector((state: RootState) => state.theme);

  return (
    <Box sx={{ ...sx }}>
      <Typography variant="subtitle2" gutterBottom>
        {label}
      </Typography>

      {/* Preview swatch */}
      <Box display="flex" alignItems="center" gap={1}>
        <Box
          ref={ref}
          onClick={() => setAnchor(ref.current)}
          sx={{
            width: 40,
            height: 40,
            borderRadius: "4px",
            border: `2px solid ${reduxTheme.grey_colors[700]}`,
            backgroundColor: isValidHex(value) ? value : reduxTheme.grey_colors[800],
            flexShrink: 0,
            cursor: "pointer",
            transition: "transform 0.15s",
            "&:hover": { transform: "scale(1.05)" },
          }}
        />
      </Box>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{ paper: { sx: { p: 1.5, borderRadius: 2 } } }}
      >
        <HexColorPicker
          color={isValidHex(value) ? value : "#000000"}
          onChange={onChange}
        />

        {/* Hex input */}
        <input
          type="text"
          value={value}
          onChange={(e) => {
            if (isValidHex(e.target.value)) onChange(e.target.value);
          }}
          placeholder="#000000"
          style={{
            width: "100%",
            marginTop: 8,
            padding: "6px 10px",
            borderRadius: 6,
            border: `1px solid ${reduxTheme.grey_colors[700]}`,
            background: "transparent",
            color: "inherit",
            fontFamily: "monospace",
            fontSize: 13,
            textAlign: "center",
            boxSizing: "border-box",
          }}
        />

        {/* Predefined swatches */}
        <Grid container spacing={1} sx={{ mt: 1 }}>
          {predefinedColors.map((color, index) => (
            <Grid key={index}>
              <Button
                variant="outlined"
                onClick={() => { onChange(color); setAnchor(null); }}
                sx={{
                  minWidth: 32,
                  height: 32,
                  borderRadius: 10,
                  backgroundColor: color,
                  borderColor:
                    value.toUpperCase() === color.toUpperCase()
                      ? reduxTheme.grey_colors[400]
                      : reduxTheme.grey_colors[700],
                  borderWidth:
                    value.toUpperCase() === color.toUpperCase() ? "2px" : "1px",
                  "&:hover": {
                    backgroundColor: color,
                    opacity: 0.8,
                    borderColor: reduxTheme.grey_colors[400],
                  },
                  "&:active": { transform: "scale(0.95)" },
                }}
              />
            </Grid>
          ))}
        </Grid>
      </Popover>
    </Box>
  );
};

export default ColorPicker;

ColorPicker.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
  sx: PropTypes.object,
};